import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, Link } from "react-router-dom";
import { HelpCircle, X, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { GuideRenderer } from "./GuideRenderer";

// Route → guide section `module` (from /help/dynamic). Longest prefix wins; "/" = Command Center.
const ROUTE_MODULE = [
  ["/leads", "/pipeline"], ["/accounts", "/accounts"], ["/queue", "/queue"], ["/admin/tenants", "/"], ["/audit", "/spend"], ["/network", "/accounts"],
];
const moduleFor = (path) => {
  if (path === "/") return "/";
  const alias = ROUTE_MODULE.find(([p]) => path.startsWith(p));
  return alias ? alias[1] : "/" + path.split("/")[1];
};

export default function HelpButton() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [guide, setGuide] = useState(null);
  const mod = moduleFor(loc.pathname);

  useEffect(() => {
    if (open && guide === null) api.get("/help/dynamic").then((r) => setGuide(r.data)).catch(() => setGuide(false));
  }, [open]); // eslint-disable-line

  const sections = guide ? guide.sections.filter((s) => s.module === mod) : [];
  const shown = sections.length ? sections : (guide ? guide.sections.slice(0, 1) : []);
  const faqs = guide ? guide.faqs.filter((f) => f.module === mod) : [];
  const content = shown.map((s) => [`${s.num}. ${s.title}`, ...s.lines].join("\n")).join("\n");
  const topic = shown[0]?.title || "Help";

  return (
    <>
      <button data-testid="help-button" onClick={() => setOpen(true)} title={`Help: ${topic}`}
        className="flex items-center gap-1.5 border border-[#DFDAD0] px-2.5 py-1.5 text-xs text-[#1C2530]/60 hover:bg-[#EFECE5] rounded-sm">
        <HelpCircle className="w-3.5 h-3.5" /> Help
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-50" data-testid="help-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#F5F3EE] shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#DFDAD0] bg-white">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Help for this page</div>
                <div className="text-lg font-semibold text-[#1C2530]" data-testid="help-panel-topic">{topic}</div>
              </div>
              <button data-testid="help-panel-close" onClick={() => setOpen(false)} className="p-1.5 text-[#1C2530]/60 hover:text-[#1C2530]"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {guide === null ? <div className="text-sm text-[#1C2530]/40 animate-pulse">Loading…</div>
                : !guide ? <div className="text-sm text-[#1C2530]/60">Could not load help. <Link to="/help" onClick={() => setOpen(false)} className="underline">Open the Help Center</Link>.</div>
                : (<>
                  {!sections.length && <div className="font-mono text-[10px] uppercase tracking-wider text-[#b1560f]" data-testid="help-no-topic">No dedicated topic for this page — showing Getting started.</div>}
                  <GuideRenderer content={content} compact />
                  {faqs.length > 0 && (
                    <div data-testid="help-panel-faqs">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-2">Related questions</div>
                      {faqs.map((f, i) => <details key={i} className="border border-[#DFDAD0] bg-white mb-1.5"><summary className="px-3 py-2 text-sm font-medium cursor-pointer">{f.q}</summary><div className="px-3 pb-3 text-sm text-[#1C2530]/80">{f.a}</div></details>)}
                    </div>
                  )}
                </>)}
            </div>
            <div className="px-5 py-3 border-t border-[#DFDAD0] bg-white">
              <Link to="/help" onClick={() => setOpen(false)} data-testid="help-open-center"
                className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#1C2530]/70 hover:text-[#1C2530]">
                <ExternalLink className="w-3.5 h-3.5" /> Open full Help Center
              </Link>
            </div>
          </div>
        </div>, document.body)}
    </>
  );
}
